import { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef, type ReactNode } from 'react';
import { api, API_BASE } from '@/lib/api';
import { useQueryClient } from '@tanstack/react-query';
import { normalizeRoles, hasRole as hasNormalizedRole, hasAnyRole as hasAnyNormalizedRole } from '@/lib/permissions';
import type { UserWithRoles } from '@/lib/types';

interface AuthContextType {
  user: UserWithRoles | null;
  loading: boolean;
  switching: boolean;
  login: (organizationCode: string, uniqueId: string, password: string) => Promise<void>;
  logout: () => void;
  refreshUser: () => Promise<void>;
  switchUser: (targetUserId: string) => Promise<void>;
  hasRole: (role: string) => boolean;
  hasAnyRole: (roles: string[]) => boolean;
  hasPermission: (permission: string) => boolean;
  hasAnyPermission: (permissions: string[]) => boolean;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UserWithRoles | null>(null);
  const [loading, setLoading] = useState(true);
  const [switching, setSwitching] = useState(false);
  const queryClient = useQueryClient();
  const userRef = useRef<UserWithRoles | null>(null);

  const refreshUser = useCallback(async () => {
    try {
      const token = sessionStorage.getItem("access_token");
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (token) headers.Authorization = `Bearer ${token}`;
      const response = await fetch(`${API_BASE}/auth/me`, { headers, credentials: 'include' });
      if (response.status === 401) {
        sessionStorage.removeItem("access_token");
        setUser(null);
        return;
      }
      if (!response.ok) { setUser(null); return; }
      const body = await response.json();
      if (body.success) {
        const next = body.user as UserWithRoles;
        const prev = userRef.current;
        const changed = next?.id !== prev?.id
          || (next?.roles?.join('|') ?? '') !== (prev?.roles?.join('|') ?? '')
          || (next?.permissions?.join('|') ?? '') !== (prev?.permissions?.join('|') ?? '');
        userRef.current = next ?? null;
        if (changed) queryClient.clear();
        setUser(next ?? null);
      }
      else setUser(null);
    } catch {
      setUser(null);
    }
  }, [queryClient]);

  const bootstrapRef = useRef(false);

  useEffect(() => {
    if (bootstrapRef.current) return;
    bootstrapRef.current = true;

    // A visitor with no stored token has nothing to validate. Skip the /auth/me
    // probe entirely so the public Login page never emits auth errors or 401s
    // just because the user has not signed in yet. Returning users with a token
    // are still verified against /auth/me.
    if (!sessionStorage.getItem("access_token")) {
      setUser(null);
      setLoading(false);
      return;
    }

    refreshUser().finally(() => setLoading(false));
  }, [refreshUser]);

  const login = useCallback(async (organizationCode: string, uniqueId: string, password: string) => {
    const res = await api.post<{ success: boolean; accessToken: string; user: UserWithRoles }>('/auth/login', { organizationCode, uniqueId, password });
    if (res.success) {
      if (res.accessToken) {
        sessionStorage.setItem("access_token", res.accessToken);
      }
      userRef.current = res.user;
      setUser(res.user);
      queryClient.clear();
    }
  }, [queryClient]);

  const logout = useCallback(() => {
    api.post('/auth/logout').finally(() => {
      sessionStorage.removeItem("access_token");
      userRef.current = null;
      setUser(null);
      queryClient.clear();
      window.location.href = '/login';
    });
  }, [queryClient]);

  const switchUser = useCallback(async (targetUserId: string) => {
    setSwitching(true);
    try {
      const token = sessionStorage.getItem("access_token");
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (token) headers.Authorization = `Bearer ${token}`;

      const res = await fetch(`${API_BASE}/auth/switch-user`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({ targetUserId }),
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || 'Switch failed');
      }

      const data = await res.json();
      if (data.token) {
        sessionStorage.setItem("access_token", data.token);
      }
      userRef.current = data.user;
      setUser(data.user);
      queryClient.clear();
      await refreshUser();
    } finally {
      setSwitching(false);
    }
  }, [queryClient, refreshUser]);

  const normalizedRoles = useMemo(() => (user ? normalizeRoles(user.roles) : []), [user]);

  const hasRole = useCallback((role: string) =>
    hasNormalizedRole(normalizedRoles, role), [normalizedRoles]);

  const hasAnyRole = useCallback((roles: string[]) =>
    hasAnyNormalizedRole(normalizedRoles, roles), [normalizedRoles]);

  const hasPermission = useCallback((permission: string) =>
    !!user?.permissions?.includes(permission), [user?.permissions]);

  const hasAnyPermission = useCallback((permissionKeys: string[]) =>
    permissionKeys.some((key) => !!user?.permissions?.includes(key)), [user?.permissions]);

  return (
    <AuthContext.Provider value={{ user, loading, switching, login, logout, refreshUser, switchUser, hasRole, hasAnyRole, hasPermission, hasAnyPermission }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
